/**
 * Sign in / sign up.
 *
 * Ported from design/Clinic Stock v2.dc.html:36-134 — the dark brand panel beside
 * a 520px form column, the tab toggle, the plan picker with a monthly/yearly
 * switch, and the 48px gold-focus fields. The prototype is the visual spec; where
 * this drifts from it, this is wrong.
 *
 * Differences from the prototype, all deliberate:
 *   - No demo-account hint block. Those were localStorage fixtures; real accounts
 *     now exist and advertising credentials on a login screen is not something to
 *     port faithfully.
 *   - "Confirm password" is validated here rather than server-side, because it is
 *     a typo check, not a security control. The API never sees it.
 */
import { useState, type FormEvent } from 'react';
import { PLAN_LABEL, PRICING, SUPPORT_EMAIL, TRIAL_DAYS, formatGBP } from '@inventory-tracker/core';
import { ApiError, login, signup, type Cycle, type Plan } from '../lib/api.js';
import { c, label, primaryButton, sans } from '../theme.js';
import { AuthLayout, ErrorBox, Field, TextLink } from './AuthLayout.js';

const PLANS: Plan[] = ['starter', 'pro', 'premium'];

interface Props {
  /** Called once the session cookie is set, so the app can re-fetch /auth/me. */
  onAuthenticated: () => void;
  /** Navigates to the forgot-password screen. */
  onForgotPassword: () => void;
  /** Which tab to open on. The landing page's two buttons choose. */
  initialMode?: 'login' | 'signup';
  /** Back to the landing page; `toPricing` also scrolls to the plans. */
  onHome: (toPricing?: boolean) => void;
}

/**
 * Two short fields side by side, dropping to one column when there is not room
 * for both. Used on sign-up so the form fits one screen.
 */
const pair: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: 14,
  alignItems: 'start',
};

export function AuthScreen({
  onAuthenticated,
  onForgotPassword,
  initialMode = 'login',
  onHome,
}: Props) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [clinicName, setClinicName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [plan, setPlan] = useState<Plan>('pro');
  const [cycle, setCycle] = useState<Cycle>('monthly');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const isSignup = mode === 'signup';

  function switchMode(next: 'login' | 'signup') {
    setMode(next);
    setError('');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (isSignup && password !== confirm) {
      setError('Those passwords do not match');
      return;
    }

    setBusy(true);
    try {
      if (isSignup) {
        await signup({ clinicName, ownerName, email, password, plan, cycle });
      } else {
        await login(email, password);
      }
      onAuthenticated();
    } catch (err) {
      // The API's messages are already written for a person to read — "Email or
      // password is incorrect", "An account already exists for that email" — so
      // they are shown as-is rather than translated into something vaguer.
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      heading={isSignup ? 'Set up your clinic' : 'Welcome back'}
      intro={
        isSignup
          ? `Free for ${TRIAL_DAYS} days. No card needed.`
          : 'Use the email you signed up with.'
      }
      onHome={() => onHome()}
      // Sign-up is a long form and gets the wider card; both are centred.
      centered={isSignup}
      aside={
        isSignup
          ? {
              lead: 'Start with what is on the shelf today.',
              rest: 'Add your products, or import a spreadsheet, and the first expiry check runs straight away.',
            }
          : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          gap: 5,
          background: c.well,
          borderRadius: 18,
          padding: 4,
          marginBottom: 22,
        }}
      >
        <Tab active={!isSignup} onClick={() => switchMode('login')}>
          Log in
        </Tab>
        <Tab active={isSignup} onClick={() => switchMode('signup')}>
          Sign up
        </Tab>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {isSignup && (
          <>
            <div style={pair}>
              <Field
                id="clinicName"
                labelText="Business name"
                placeholder="Your clinic's name"
                value={clinicName}
                onChange={setClinicName}
                autoComplete="organization"
                required
              />
              <Field
                id="ownerName"
                labelText="Your name"
                placeholder="First and last name"
                value={ownerName}
                onChange={setOwnerName}
                autoComplete="name"
                required
              />
            </div>
            <PlanPicker plan={plan} cycle={cycle} onPlan={setPlan} onCycle={setCycle} />
          </>
        )}

        <Field
          id="email"
          labelText="Email"
          type="email"
          placeholder="you@yourclinic.co.uk"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <div style={isSignup ? pair : undefined}>
          <Field
            id="password"
            labelText="Password"
            type="password"
            placeholder={isSignup ? 'At least 10 characters' : 'Your password'}
            value={password}
            onChange={setPassword}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            required
            hint={isSignup ? 'A long phrase is stronger than symbols.' : undefined}
          />
          {isSignup && (
            <Field
              id="confirm"
              labelText="Confirm password"
              type="password"
              placeholder="Type it again"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              required
            />
          )}
        </div>

        {error && <ErrorBox>{error}</ErrorBox>}

        <button
          type="submit"
          disabled={busy}
          style={{
            ...primaryButton,
            marginTop: 4,
            opacity: busy ? 0.65 : 1,
            cursor: busy ? 'progress' : 'pointer',
          }}
        >
          {busy
            ? isSignup
              ? 'Creating your account…'
              : 'Signing in…'
            : isSignup
              ? 'Create account'
              : 'Log in'}
        </button>

        {isSignup && (
          <p
            style={{
              textAlign: 'center',
              fontSize: 12.5,
              color: c.muted,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            By creating an account you agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" style={{ color: c.green }}>
              terms of service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: c.green }}>
              privacy policy
            </a>
            .
          </p>
        )}

        {/* Sign-up's heading already says the trial is free and needs no card. */}
        {!isSignup && (
          <div style={{ textAlign: 'center', marginTop: 2 }}>
            <TextLink onClick={onForgotPassword}>Forgotten your password?</TextLink>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <TextLink onClick={() => onHome(true)}>See plans and pricing</TextLink>
        </div>

        <p style={{ textAlign: 'center', fontSize: 13, color: c.muted, margin: '2px 0 0' }}>
          Need help?{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: c.green }}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        height: 44,
        border: 'none',
        borderRadius: 980,
        cursor: 'pointer',
        fontSize: 15,
        fontWeight: 500,
        fontFamily: sans,
        background: active ? c.white : 'transparent',
        color: active ? c.ink : c.muted,
        boxShadow: active ? '0 1px 2px rgba(20,26,22,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PlanPicker({
  plan,
  cycle,
  onPlan,
  onCycle,
}: {
  plan: Plan;
  cycle: Cycle;
  onPlan: (p: Plan) => void;
  onCycle: (cy: Cycle) => void;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 7,
        }}
      >
        <span style={{ ...label, marginBottom: 0 }}>Choose your plan</span>
        <div
          style={{
            display: 'inline-flex',
            gap: 3,
            background: c.well,
            border: `1px solid ${c.borderStrong}`,
            borderRadius: 18,
            padding: 3,
          }}
        >
          {(['monthly', 'yearly'] as Cycle[]).map((cy) => (
            <button
              key={cy}
              type="button"
              onClick={() => onCycle(cy)}
              style={{
                border: 'none',
                borderRadius: 980,
                padding: '6px 11px',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: sans,
                cursor: 'pointer',
                background: cycle === cy ? c.white : 'transparent',
                color: cycle === cy ? c.ink : c.muted,
              }}
            >
              {cy === 'monthly' ? 'Monthly' : 'Yearly'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {PLANS.map((p) => {
          const selected = p === plan;
          // Prices come from packages/core, so this cannot drift from what the
          // API charges or what Stripe was configured with.
          const pence = cycle === 'monthly' ? PRICING[p].monthly : PRICING[p].yearlyPerMonth;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPlan(p)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                padding: '12px 8px',
                borderRadius: 14,
                cursor: 'pointer',
                fontFamily: sans,
                textAlign: 'center',
                background: selected ? c.greenDeep : c.white,
                border: `1px solid ${selected ? c.greenDeep : c.borderStrong}`,
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: selected ? c.onDarkBright : c.ink,
                }}
              >
                {PLAN_LABEL[p]}
              </span>
              <span style={{ fontSize: 11.5, color: selected ? c.onDarkSoft : c.muted }}>
                {formatGBP(pence)}/mo
              </span>
            </button>
          );
        })}
      </div>

      {cycle === 'yearly' && (
        <div style={{ fontSize: 11, color: c.success, fontWeight: 500, marginTop: 7 }}>
          ✓ Annual billing: 2 months free, save up to 20%
        </div>
      )}
    </div>
  );
}
